/**
 * Oeffentliche Oeffnungszeiten-Seite (v2.9.11)
 * Design: GB Sylt Stil (#005ca8 Blau, Arial, weiss)
 * Auto-Refresh via Polling (30s Intervall mit Versions-Check)
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { Clock, Phone, Mail, ExternalLink, AlertCircle, MapPin } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BLUE = '#005ca8';
const CLOSED_RED = '#dc2626';
const REFRESH_INTERVAL = 30000;

const highlightClosed = (text) => {
  if (!text || typeof text !== 'string') return text;
  const parts = text.split(/(geschlossen)/i);
  if (parts.length === 1) return text;
  return parts.map((part, i) =>
    part.toLowerCase() === 'geschlossen'
      ? <span key={i} style={{ color: CLOSED_RED, fontWeight: 600 }}>{part}</span>
      : part
  );
};

const formatTimes = (times) => {
  if (!times || times.length === 0) return null;
  return times.map(t => `${t.from} – ${t.to} Uhr`).join(', ');
};

export default function OeffnungszeitenPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const versionRef = useRef(0);

  const loadData = useCallback(async (initial = false) => {
    try {
      const res = await fetch(`${API}/opening-hours/public`);
      const json = await res.json();
      const newVersion = json.version || 0;
      if (initial || newVersion !== versionRef.current) {
        setData(json);
        versionRef.current = newVersion;
      }
    } catch (err) {
      console.error('Oeffnungszeiten laden fehlgeschlagen:', err);
    } finally {
      if (initial) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData(true);
    const interval = setInterval(() => loadData(false), REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [loadData]);

  // Scroll-Fix: Sicherstellen, dass body/html scrollbar sind
  useEffect(() => {
    document.documentElement.style.overflowY = 'auto';
    document.body.style.overflowY = 'auto';
    return () => {
      document.documentElement.style.overflowY = '';
      document.body.style.overflowY = '';
    };
  }, []);

  if (loading) {
    return (
      <div style={{ fontFamily: 'Arial, sans-serif', display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', background: '#fff' }}>
        <div style={{ textAlign: 'center', color: '#666' }}>
          <Clock style={{ width: 40, height: 40, margin: '0 auto 12px', color: BLUE }} />
          <p>Wird geladen...</p>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { today, week, global_notice, contact, texts, upcoming_exceptions, next_week_closures } = data;
  const t = texts || {};

  return (
    <div style={{ fontFamily: 'Arial, sans-serif', background: '#fff', minHeight: '100vh', color: '#222' }} data-testid="oeffnungszeiten-page">
      <div style={{ maxWidth: 720, margin: '0 auto', padding: '32px 20px 60px' }}>

        {/* Header */}
        <header style={{ textAlign: 'center', marginBottom: 32 }}>
          <h1 style={{ color: BLUE, fontSize: 28, fontWeight: 700, margin: 0 }} data-testid="oh-title">
            {t.title || 'Oeffnungszeiten'}
          </h1>
          <p style={{ color: '#666', fontSize: 15, marginTop: 6 }}>
            {t.subtitle || 'Alle aktuellen Zeiten auf einen Blick'}
          </p>
        </header>

        {/* Globaler Hinweis */}
        {global_notice?.active && global_notice?.text && (
          <div style={{
            background: '#FFF3CD', border: '1px solid #FFECB5', borderRadius: 10,
            padding: '14px 18px', marginBottom: 24, display: 'flex', alignItems: 'flex-start', gap: 10
          }} data-testid="global-notice">
            <AlertCircle style={{ width: 20, height: 20, color: '#856404', flexShrink: 0, marginTop: 2 }} />
            <p style={{ margin: 0, color: '#664D03', fontSize: 14, lineHeight: 1.5 }}>{global_notice.text}</p>
          </div>
        )}

        {/* HEUTE */}
        <section style={{
          background: today?.is_open ? '#E8F5E9' : '#FFEBEE',
          border: `2px solid ${today?.is_open ? '#66BB6A' : '#EF5350'}`,
          borderRadius: 14, padding: '20px 24px', marginBottom: 28
        }} data-testid="today-section">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <h2 style={{ color: today?.is_open ? '#2E7D32' : '#444', fontSize: 20, fontWeight: 700, margin: 0 }} data-testid="today-status">
              {today?.is_open ? (t.today_open || 'Heute geoeffnet') : highlightClosed(t.today_closed || 'Heute geschlossen')}
            </h2>
            <span style={{
              background: today?.is_open ? '#66BB6A' : '#EF5350', color: '#fff',
              padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600
            }}>
              {today?.day_name}
            </span>
          </div>

          {today?.notice && (
            <p style={{ color: '#555', fontSize: 13, margin: '0 0 10px', fontStyle: 'italic' }}>
              {today.notice}
            </p>
          )}

          {today?.is_open && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                  <MapPin style={{ width: 14, height: 14, color: BLUE }} />
                  <span style={{ fontSize: 13, fontWeight: 600, color: BLUE }}>{t.office_label || 'Buero'}</span>
                </div>
                {today.office_times?.length > 0 ? (
                  today.office_times.map((slot, i) => (
                    <p key={i} style={{ margin: '2px 0', fontSize: 15, fontWeight: 500 }}>
                      {slot.from} – {slot.to} Uhr
                    </p>
                  ))
                ) : (
                  <p style={{ margin: 0, fontSize: 14, color: CLOSED_RED, fontWeight: 500 }}>{t.closed_label || 'Geschlossen'}</p>
                )}
              </div>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                  <Phone style={{ width: 14, height: 14, color: BLUE }} />
                  <span style={{ fontSize: 13, fontWeight: 600, color: BLUE }}>{t.phone_label || 'Telefon'}</span>
                </div>
                {today.phone_times?.length > 0 ? (
                  today.phone_times.map((slot, i) => (
                    <p key={i} style={{ margin: '2px 0', fontSize: 15, fontWeight: 500 }}>
                      {slot.from} – {slot.to} Uhr
                    </p>
                  ))
                ) : (
                  <p style={{ margin: 0, fontSize: 14, color: '#999' }}>{t.phone_not_reachable || 'Nicht besetzt'}</p>
                )}
              </div>
            </div>
          )}

          {today?.is_open && today?.lunch_break && (
            <p style={{ marginTop: 10, fontSize: 13, color: '#666' }}>
              {t.lunch_label || 'Mittagspause'}: {today.lunch_break}
            </p>
          )}
        </section>

        {/* Wochenuebersicht */}
        <section style={{ marginBottom: 32 }}>
          <h2 style={{ color: BLUE, fontSize: 18, fontWeight: 700, marginBottom: 14 }}>
            {t.week_overview || 'Wochenuebersicht'}
          </h2>
          <div style={{ borderRadius: 10, overflow: 'hidden', border: '1px solid #E0E0E0' }} data-testid="week-overview">
            {week?.map((day, i) => (
              <div key={day.date} style={{
                display: 'grid', gridTemplateColumns: '120px 1fr 1fr',
                padding: '10px 16px', borderBottom: i < 6 ? '1px solid #EEE' : 'none',
                background: day.is_today ? '#E3F2FD' : (i % 2 === 0 ? '#FAFAFA' : '#fff'),
                alignItems: 'center',
              }} data-testid={`week-day-${day.weekday}`}>
                <div>
                  <span style={{ fontWeight: day.is_today ? 700 : 500, fontSize: 14, color: day.is_today ? BLUE : '#333' }}>
                    {day.day_name}
                  </span>
                  {day.is_today && (
                    <span style={{ marginLeft: 6, fontSize: 10, background: BLUE, color: '#fff', padding: '1px 6px', borderRadius: 8, fontWeight: 600 }}>
                      Heute
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 13 }}>
                  {day.is_open ? (
                    <span style={{ color: '#333' }}>
                      <span style={{ color: '#888', fontSize: 11, marginRight: 4 }}>{t.office_label || 'Buero'}:</span>
                      {formatTimes(day.office_times) || highlightClosed(t.closed_label || 'Geschlossen')}
                    </span>
                  ) : (
                    <span style={{ color: '#555', fontWeight: 500 }}>
                      {day.notice ? (
                        <>
                          <span>{day.notice}</span>
                          {' – '}
                          {highlightClosed(t.closed_label || 'Geschlossen')}
                        </>
                      ) : highlightClosed(t.closed_label || 'Geschlossen')}
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 13 }}>
                  {day.is_open && day.phone_times?.length > 0 ? (
                    <span style={{ color: '#333' }}>
                      <span style={{ color: '#888', fontSize: 11, marginRight: 4 }}>{t.phone_label || 'Tel'}:</span>
                      {formatTimes(day.phone_times)}
                    </span>
                  ) : day.is_open ? (
                    <span style={{ color: '#999', fontSize: 12 }}>{t.phone_not_reachable || 'Tel. nicht besetzt'}</span>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Kommende Schliesstage der Folgewoche */}
        {next_week_closures?.length > 0 && (
          <section style={{ marginBottom: 32 }} data-testid="next-week-closures">
            <h2 style={{ color: BLUE, fontSize: 18, fontWeight: 700, marginBottom: 14 }}>
              Kommende Schliesstage
            </h2>
            <div style={{ borderRadius: 10, overflow: 'hidden', border: '1px solid #E0E0E0' }}>
              {next_week_closures.map((day, i) => {
                const formatted = day.date
                  ? new Date(day.date + 'T00:00:00').toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
                  : '';
                return (
                  <div key={day.date} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '10px 16px',
                    borderBottom: i < next_week_closures.length - 1 ? '1px solid #EEE' : 'none',
                    background: i % 2 === 0 ? '#FAFAFA' : '#fff',
                  }} data-testid={`closure-${day.date}`}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontWeight: 500, fontSize: 14, color: '#333', minWidth: 90 }}>
                        {day.day_name}
                      </span>
                      <span style={{ fontSize: 13, color: '#666' }}>{formatted}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {day.notice && (
                        <span style={{ fontSize: 13, color: '#555', fontStyle: 'italic' }}>{day.notice}</span>
                      )}
                      <span style={{ color: CLOSED_RED, fontWeight: 600, fontSize: 13 }}>geschlossen</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Kontaktbereich */}
        <section style={{
          background: '#F5F8FC', border: '1px solid #D6E4F0', borderRadius: 14,
          padding: '24px', marginBottom: 28
        }} data-testid="contact-section">
          <h2 style={{ color: BLUE, fontSize: 18, fontWeight: 700, marginBottom: 12 }}>
            {t.contact_title || 'Kontakt'}
          </h2>

          {!today?.is_open && contact?.closed_text && (
            <p style={{ color: '#555', fontSize: 14, marginBottom: 14, fontWeight: 500, lineHeight: 1.5 }}>
              {highlightClosed(contact.closed_text)}
            </p>
          )}

          {contact?.hint_text && (
            <p style={{ color: '#555', fontSize: 14, marginBottom: 16, lineHeight: 1.6 }}>
              {contact.hint_text}
            </p>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {contact?.email && (
              <a href={`mailto:${contact.email}`} style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '12px 18px',
                background: '#fff', border: `2px solid ${BLUE}`, borderRadius: 10,
                textDecoration: 'none', color: BLUE, fontWeight: 600, fontSize: 15,
                transition: 'background 0.2s',
              }} data-testid="contact-email">
                <Mail style={{ width: 18, height: 18 }} />
                {contact.email}
              </a>
            )}

            {contact?.form_link && (
              <a href={contact.form_link} target="_blank" rel="noopener noreferrer" style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '12px 18px',
                background: BLUE, border: `2px solid ${BLUE}`, borderRadius: 10,
                textDecoration: 'none', color: '#fff', fontWeight: 600, fontSize: 15,
              }} data-testid="contact-form-link">
                <ExternalLink style={{ width: 18, height: 18 }} />
                Kontaktformular
              </a>
            )}
          </div>
        </section>

        {/* Footer */}
        <footer style={{ textAlign: 'center', color: '#AAA', fontSize: 12, marginTop: 20 }}>
          GB Sylt Hausverwaltung
        </footer>
      </div>
    </div>
  );
}
